import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ConnectionState, Track } from 'livekit-client'
import { RoomContext, useVoiceAssistant, useLocalParticipant } from '@livekit/components-react'
import { useLiveKit } from '@/hooks/useLiveKit'
import { useAuth } from '@/hooks/useAuth'
import { useAgentTranscription } from '@/hooks/useAgentTranscription'
import { useVisualTools } from '@/hooks/useVisualTools'
import { useNetworkToasts } from '@/hooks/useNetworkToasts'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { ConnectionStatusBadge } from '@/components/ui/connection-status-badge'
import { TranscriptionDisplay } from '@/components/ui/transcription-display'
import { WaveformVisualizer } from '@/components/ui/waveform-visualizer'
import { VisualToolsOverlay } from '@/components/visual-tools'
import { analytics } from '@/lib/analytics'
import { Mic, MicOff, Square, AlertCircle } from 'lucide-react'
import type { OnboardingStatus } from '@/types/database'

// Agent audio visualizer component - uses RoomContext
function AgentAudioVisualizer({ isAgentConnected }: { isAgentConnected: boolean }) {
  const { state, audioTrack } = useVoiceAssistant()

  // Show waiting state if agent hasn't connected yet
  if (!isAgentConnected) {
    return (
      <div className="flex flex-col items-center py-4">
        <div className="relative">
          {/* Pulsing ring */}
          <div className="absolute inset-0 rounded-full bg-amber-400/20 animate-ping" />
          {/* Kai avatar */}
          <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 border-2 border-white shadow-lg flex items-center justify-center">
            <svg viewBox="0 0 100 100" className="w-12 h-12">
              <circle cx="50" cy="50" r="45" fill="#FEF3C7" />
              <circle cx="25" cy="55" r="8" fill="#FECACA" opacity="0.6" />
              <circle cx="75" cy="55" r="8" fill="#FECACA" opacity="0.6" />
              <ellipse cx="35" cy="42" rx="6" ry="7" fill="#1F2937" />
              <ellipse cx="65" cy="42" rx="6" ry="7" fill="#1F2937" />
              <circle cx="37" cy="40" r="2" fill="white" />
              <circle cx="67" cy="40" r="2" fill="white" />
              <path d="M 35 62 Q 50 78 65 62" fill="none" stroke="#1F2937" strokeWidth="3" strokeLinecap="round" />
            </svg>
          </div>
        </div>
        <p className="text-xs text-stone-500 mt-3">Waiting for Kai to join...</p>
      </div>
    )
  }

  // Normal state - show waveform
  return (
    <div className="flex flex-col items-center py-4">
      <div className="w-72 h-16">
        <WaveformVisualizer
          trackRef={audioTrack}
          color="#57534e" // stone-600 for agent
          lineWidth={2}
        />
      </div>
      <p className="text-xs text-stone-500 mt-2">
        {state === 'speaking'
          ? 'Kai is speaking...'
          : state === 'thinking'
            ? 'Kai is thinking...'
            : 'Listening...'}
      </p>
    </div>
  )
}

// User audio level indicator component - uses RoomContext
function UserAudioVisualizer() {
  const { localParticipant } = useLocalParticipant()

  const audioTrack = useMemo(() => {
    if (!localParticipant) return undefined
    for (const pub of localParticipant.audioTrackPublications.values()) {
      if (pub.track && pub.source === Track.Source.Microphone) {
        // Include publication for useMultibandTrackVolume to work
        return {
          participant: localParticipant,
          source: Track.Source.Microphone,
          publication: pub,
        }
      }
    }
    return undefined
  }, [localParticipant])

  if (!audioTrack) {
    return null
  }

  return (
    <div className="flex justify-center py-2">
      <div className="w-64 h-8">
        <WaveformVisualizer
          trackRef={audioTrack}
          color="#a8a29e" // stone-400 for user (lighter)
          lineWidth={1.5}
        />
      </div>
    </div>
  )
}

// Generating roadmap loading state component for introduction sessions
function GeneratingRoadmapState() {
  return (
    <div className="h-screen bg-gradient-to-br from-amber-50/50 via-white to-orange-50/50 flex flex-col overflow-hidden">
      <div className="w-full max-w-2xl mx-auto flex flex-col flex-1 p-4 justify-center">
        <div className="relative">
          {/* Decorative background elements */}
          <div className="absolute -top-10 -left-10 w-40 h-40 bg-gradient-to-br from-amber-200/40 to-orange-200/40 rounded-full blur-3xl" />
          <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-gradient-to-br from-orange-200/30 to-rose-200/30 rounded-full blur-3xl" />

          {/* Main card */}
          <div className="relative bg-gradient-to-br from-amber-50/80 via-white to-orange-50/80 rounded-3xl border border-amber-200/50 shadow-xl overflow-hidden">
            {/* Top decorative bar with animation */}
            <div className="h-2 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-pulse" />
            </div>

            <div className="p-8 md:p-10">
              {/* Loading progress section */}
              <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-amber-100/50">
                <div className="flex items-center gap-4">
                  {/* Animated loader */}
                  <div className="relative w-12 h-12 flex-shrink-0">
                    <div className="absolute inset-0 rounded-full border-4 border-amber-200" />
                    <div className="absolute inset-0 rounded-full border-4 border-amber-500 border-t-transparent animate-spin" />
                  </div>

                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-amber-700 mb-1">
                      Creating Your Personalized Roadmap
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Analyzing your goals, interests, and level...
                    </p>
                  </div>
                </div>

                {/* Progress steps */}
                <div className="mt-6 space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-700">Introduction completed</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-700">Level assessed</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 rounded-full bg-amber-400 flex items-center justify-center flex-shrink-0 animate-pulse">
                      <div className="w-2 h-2 rounded-full bg-white" />
                    </div>
                    <span className="text-gray-700">Generating personalized units...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Type for navigation state passed from Home.tsx
interface PracticeLocationState {
  lessonType?: string
  unitId?: string
}

// Lesson types that have highlights/session results
const LESSON_TYPES_WITH_HIGHLIGHTS = ['learn', 'warmup', 'vocabulary', 'grammar', 'practice', 'conversation']

export function Practice() {
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as PracticeLocationState | null
  const { profile, session, isLoading: isAuthLoading } = useAuth()

  const [duration, setDuration] = useState(0)
  const [hasStarted, setHasStarted] = useState(false)
  const [isGeneratingRoadmap, setIsGeneratingRoadmap] = useState(false)
  const sessionIdRef = useRef<string | null>(null)
  const durationRef = useRef(0)
  const muteStartTimeRef = useRef<number | null>(null)
  const sessionStartedTrackedRef = useRef(false)
  const isInitialMountRef = useRef(true)

  const {
    handleLocalQualityChanged,
    handleAgentQualityChanged,
    handleReconnecting,
    handleReconnected,
    handleAgentConnected,
    handleAgentDisconnected,
  } = useNetworkToasts()

  const {
    room,
    sessionId,
    connectionState,
    isConnecting,
    isConnected,
    isWaitingForAgent,
    agentTimedOut,
    error,
    isMuted,
    networkState,
    connect,
    disconnect,
    toggleMute,
    retryAgentConnection,
  } = useLiveKit({
    session,
    onConnected: () => {
      setHasStarted(true)
    },
    onDisconnected: () => {
      // Could be network issue - stay on page for potential reconnection
    },
    onRoomDeleted: async () => {
      // Agent ended call - navigate based on lesson type
      if (sessionIdRef.current) {
        analytics.sessionEnded(sessionIdRef.current, durationRef.current, 'agent')

        const lessonType = locationState?.lessonType

        // For introduction sessions: show roadmap generation state
        if (lessonType === 'introduction' && profile?.id) {
          // Update onboarding_status to trigger roadmap generation
          await supabase
            .from('users')
            .update({ onboarding_status: 'intro_completed' } as never)
            .eq('id', profile.id)

          setIsGeneratingRoadmap(true)
          return
        }

        // Checkpoint sessions don't have highlights - go home
        if (lessonType && !LESSON_TYPES_WITH_HIGHLIGHTS.includes(lessonType)) {
          navigate('/')
        } else {
          // For regular lessons: navigate to session results
          navigate(`/session/${sessionIdRef.current}`)
        }
      }
    },
    onLocalQualityChanged: handleLocalQualityChanged,
    onAgentQualityChanged: handleAgentQualityChanged,
    onReconnecting: handleReconnecting,
    onReconnected: handleReconnected,
    onAgentConnected: () => {
      handleAgentConnected()
      if (sessionIdRef.current) {
        analytics.sessionAgentJoined(sessionIdRef.current)
      }
    },
    onAgentDisconnected: handleAgentDisconnected,
  })

  const { messages: transcriptionMessages, interimText } = useAgentTranscription({ room })
  const { state: visualToolsState, clearDisplay: clearVisualTools } = useVisualTools(room)

  // Keep sessionId in ref for callbacks
  useEffect(() => {
    sessionIdRef.current = sessionId
  }, [sessionId])

  // Track session started when connected with sessionId
  useEffect(() => {
    if (isConnected && sessionId && !sessionStartedTrackedRef.current) {
      sessionStartedTrackedRef.current = true
      analytics.sessionStarted(sessionId)
    }
  }, [isConnected, sessionId])

  const startSession = useCallback(async () => {
    await connect({
      lessonType: locationState?.lessonType,
      unitId: locationState?.unitId,
    })
  }, [connect, locationState])

  const endSession = useCallback(async () => {
    if (sessionId) {
      analytics.sessionEnded(sessionId, duration, 'user')
    }
    await disconnect()

    const lessonType = locationState?.lessonType

    // For introduction sessions: show roadmap generation state
    if (lessonType === 'introduction' && profile?.id) {
      // Update onboarding_status to trigger roadmap generation
      await supabase
        .from('users')
        .update({ onboarding_status: 'intro_completed' } as never)
        .eq('id', profile.id)

      setIsGeneratingRoadmap(true)
      return
    }

    if (sessionId) {
      // Checkpoint sessions don't have highlights - go home
      if (lessonType && !LESSON_TYPES_WITH_HIGHLIGHTS.includes(lessonType)) {
        navigate('/')
      } else {
        navigate(`/session/${sessionId}`)
      }
    } else {
      navigate('/')
    }
  }, [disconnect, sessionId, navigate, duration, locationState?.lessonType, profile?.id])

  const handleToggleMute = useCallback(() => {
    if (sessionId) {
      if (isMuted) {
        // Currently muted, about to unmute
        const mutedDuration = muteStartTimeRef.current
          ? Math.floor((Date.now() - muteStartTimeRef.current) / 1000)
          : 0
        analytics.sessionUnmuted(sessionId, mutedDuration)
        muteStartTimeRef.current = null
      } else {
        // Currently unmuted, about to mute
        muteStartTimeRef.current = Date.now()
        analytics.sessionMuted(sessionId, duration)
      }
    }
    toggleMute()
  }, [sessionId, isMuted, toggleMute, duration])

  // Timer for session duration
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>
    if (isConnected) {
      interval = setInterval(() => {
        setDuration(d => {
          const newDuration = d + 1
          durationRef.current = newDuration
          return newDuration
        })
      }, 1000)
    }
    return () => clearInterval(interval)
  }, [isConnected])

  // Auto-connect on initial mount only, after auth is ready
  useEffect(() => {
    if (isInitialMountRef.current && !isAuthLoading && session) {
      isInitialMountRef.current = false
      startSession()
    }
  }, [startSession, isAuthLoading, session])

  // Poll for onboarding status when generating roadmap
  useEffect(() => {
    if (!isGeneratingRoadmap || !profile?.id) return

    const pollInterval = setInterval(async () => {
      const { data } = await supabase
        .from('users')
        .select('onboarding_status')
        .eq('id', profile.id)
        .single()

      const status = (data as { onboarding_status: OnboardingStatus } | null)?.onboarding_status
      if (status === 'ready') {
        // Roadmap is ready, navigate to home
        navigate('/')
      }
    }, 3000) // Poll every 3 seconds

    return () => clearInterval(pollInterval)
  }, [isGeneratingRoadmap, profile?.id, navigate])

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const getStatusText = () => {
    // Check networkState.isReconnecting first - more reliable during network issues
    if (networkState.isReconnecting) {
      return 'Connection lost - Reconnecting...'
    }

    if (agentTimedOut) {
      return 'Tutor unavailable'
    }

    if (isWaitingForAgent) {
      return 'Starting tutor...'
    }

    switch (connectionState) {
      case ConnectionState.Connecting:
        return 'Connecting...'
      case ConnectionState.Connected:
        return 'Practicing with Kai'
      case ConnectionState.Reconnecting:
        return 'Reconnecting...'
      case ConnectionState.Disconnected:
        return hasStarted ? 'Session ended' : 'Ready to start'
      default:
        // Only show initializing before first connection attempt
        return hasStarted ? 'Reconnecting...' : 'Initializing...'
    }
  }

  // Show loading state while auth is initializing
  if (isAuthLoading) {
    return (
      <div className="h-screen bg-gradient-to-br from-amber-50/50 via-white to-orange-50/50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-stone-500">Loading...</p>
        </div>
      </div>
    )
  }

  // Show roadmap generation state for introduction sessions
  if (isGeneratingRoadmap) {
    return <GeneratingRoadmapState />
  }

  return (
    <div className="h-screen bg-gradient-to-br from-amber-50/50 via-white to-orange-50/50 flex flex-col overflow-hidden">
      <div className="w-full max-w-2xl mx-auto flex flex-col flex-1 p-4 min-h-0">
        {/* Warm header */}
        <div className="flex items-center justify-between py-3 px-4 -mx-4 bg-gradient-to-r from-amber-50/60 via-white to-orange-50/60 border-b border-amber-200/30 shadow-sm">
          <h2 className="text-lg font-semibold text-stone-800">{getStatusText()}</h2>
          <div className="flex items-center gap-3">
            {isConnected && (
              <>
                <span className="text-sm font-mono text-stone-500 tabular-nums">
                  {formatDuration(duration)}
                </span>
                <ConnectionStatusBadge
                  networkState={networkState}
                  isConnected={isConnected}
                />
              </>
            )}
          </div>
        </div>

        {/* Main content area */}
        <div className="flex-1 flex flex-col py-4 min-h-0">
          {isConnected && room ? (
            <RoomContext.Provider value={room}>
              <div className="flex-1 flex flex-col min-h-0">
                {/* Agent audio visualizer */}
                <div className="shrink-0">
                  <AgentAudioVisualizer isAgentConnected={networkState.isAgentConnected} />
                </div>

                {/* Transcription area - capped height, shrinks when visual tools active */}
                <div className="flex-1 min-h-0 max-h-[350px] my-4">
                  <TranscriptionDisplay
                    messages={transcriptionMessages}
                    interimText={interimText}
                    className="w-full h-full"
                  />
                </div>

                {/* Visual Tools - integrated into layout */}
                {visualToolsState.activeType && (
                  <div className="shrink-0 my-4">
                    <VisualToolsOverlay
                      state={visualToolsState}
                      onClear={clearVisualTools}
                    />
                  </div>
                )}

                {/* User audio level indicator - anchored at bottom */}
                <div className="shrink-0 mt-auto">
                  <UserAudioVisualizer />
                  <p className="text-xs text-stone-400 text-center">
                    {isMuted ? 'Microphone muted' : 'Your microphone'}
                  </p>
                </div>

                {/* Controls area */}
                <div className="flex flex-col items-center space-y-4 pt-4 shrink-0">
                  {/* Mute button with amber styling */}
                  <Button
                    onClick={handleToggleMute}
                    className={`w-40 h-12 text-base font-medium transition-all ${
                      isMuted
                        ? 'bg-gradient-to-r from-amber-400 to-orange-400 hover:from-amber-500 hover:to-orange-500 text-white shadow-lg shadow-amber-500/25'
                        : 'bg-white/80 border-2 border-amber-300 text-stone-700 hover:bg-amber-50 hover:border-amber-400'
                    }`}
                  >
                    {isMuted ? (
                      <>
                        <MicOff className="w-5 h-5 mr-2" />
                        Unmute
                      </>
                    ) : (
                      <>
                        <Mic className="w-5 h-5 mr-2" />
                        Mute
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </RoomContext.Provider>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              {isConnecting && !isWaitingForAgent && !agentTimedOut && (
                <div className="flex flex-col items-center space-y-4">
                  <div className="w-12 h-12 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm text-stone-500">
                    Connecting...
                  </p>
                </div>
              )}

              {isWaitingForAgent && !agentTimedOut && (
                <div className="flex flex-col items-center space-y-4">
                  <div className="w-12 h-12 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm text-stone-500">Starting your tutor...</p>
                  <p className="text-xs text-stone-400">This may take up to 30 seconds</p>
                </div>
              )}

              {agentTimedOut && (
                <div className="flex flex-col items-center space-y-4">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-amber-600" />
                  </div>
                  <div className="text-center space-y-1">
                    <p className="text-sm text-stone-700">Tutor is taking longer than expected</p>
                    <p className="text-xs text-stone-400">This sometimes happens when the service is waking up</p>
                  </div>
                  <button
                    onClick={retryAgentConnection}
                    className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {(connectionState === ConnectionState.Reconnecting || networkState.isReconnecting) && (
                <div className="flex flex-col items-center space-y-4">
                  <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm text-stone-500">
                    Connection interrupted, reconnecting...
                  </p>
                </div>
              )}

              {error && !agentTimedOut && (
                <div className="text-center space-y-2">
                  <p className="text-sm text-rose-500">{error.message}</p>
                  <p className="text-xs text-stone-400">
                    Please check your internet connection and try again
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer with End Session button */}
        <div className="flex justify-center py-4 border-t border-amber-200/30">
          {isConnected ? (
            <Button
              onClick={endSession}
              className="w-48 bg-gradient-to-r from-rose-400 to-rose-500 hover:from-rose-500 hover:to-rose-600 text-white shadow-lg shadow-rose-500/25"
            >
              <Square className="w-4 h-4 mr-2 fill-current" />
              End Session
            </Button>
          ) : connectionState === ConnectionState.Disconnected && !isConnecting ? (
            <Button
              onClick={startSession}
              className="w-48 bg-gradient-to-r from-amber-400 to-orange-400 hover:from-amber-500 hover:to-orange-500 text-white shadow-lg shadow-amber-500/25"
            >
              {hasStarted ? 'Reconnect' : 'Retry'}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
